// ─── Pièces jointes (Core, réutilisable) ─────────────────────────
// Ajout depuis galerie / caméra / fichier ; miniatures pour les images, puces
// pour les autres fichiers ; ouverture au tap ; retrait par ✕ (ou appui long).
// Les modules natifs sont chargés PARESSEUSEMENT : une app qui ne les installe
// pas voit simplement moins de boutons (jamais de crash).
//   <PiecesJointes valeur={[{url,chemin,nom,type}]} dossier="evenements/xxx"
//                  onChange={setListe} lecture={false} />
import React, { useMemo, useState } from 'react';
import { View, Text, Image, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator, Platform, Linking } from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { uploadFichier, supprimerFichier } from '../services/stockage';
import { confirmerSuppression } from '../services/confirm';
import VisionneuseImage from './VisionneuseImage';

let ImagePicker = null;
try { ImagePicker = require('expo-image-picker'); } catch (_) { /* absent → bouton masqué */ }
let DocumentPicker = null;
try { DocumentPicker = require('expo-document-picker'); } catch (_) { /* absent → bouton masqué */ }

const estImage = (p) => p?.type === 'image' || /\.(jpe?g|png|gif|webp|heic)$/i.test(p?.nom || p?.url || '');

export default function PiecesJointes({ valeur = [], dossier, onChange, lecture = false }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [zoom, setZoom] = useState(null);   // image ouverte en plein écran
  const liste = valeur || [];

  async function ajouterImage(depuisCamera) {
    if (!ImagePicker || busy) return;
    setBusy(true); setErr('');
    try {
      if (depuisCamera) {
        const perm = await ImagePicker.requestCameraPermissionsAsync();
        if (!perm.granted) { setErr(t('commun.accesCameraRefuse')); setBusy(false); return; }
      }
      const res = depuisCamera
        ? await ImagePicker.launchCameraAsync({ quality: 0.6 })
        : await ImagePicker.launchImageLibraryAsync({ quality: 0.6, mediaTypes: ['images'] });
      if (!res.canceled && res.assets?.[0]?.uri) {
        const { url, chemin } = await uploadFichier(res.assets[0].uri, dossier);
        onChange([...liste, { url, chemin, nom: res.assets[0].fileName || 'image.jpg', type: 'image' }]);
      }
    } catch (e) { setErr(e?.message || String(e)); }
    setBusy(false);
  }

  async function ajouterFichier() {
    if (!DocumentPicker || busy) return;
    setBusy(true); setErr('');
    try {
      const res = await DocumentPicker.getDocumentAsync({ copyToCacheDirectory: true });
      const a = res?.assets?.[0];
      if (a?.uri) {
        const { url, chemin } = await uploadFichier(a.uri, dossier, { defautExt: (a.name || '').split('.').pop() || 'bin' });
        onChange([...liste, { url, chemin, nom: a.name || 'fichier', type: 'fichier' }]);
      }
    } catch (e) { setErr(e?.message || String(e)); }
    setBusy(false);
  }

  function retirer(i) {
    const p = liste[i];
    confirmerSuppression(t('commun.retirerPiece'), t('commun.pieceSeraRetiree', { nom: p?.nom || t('budget.fichier') }), () => {
      onChange(liste.filter((_, j) => j !== i));
      if (p?.chemin) supprimerFichier(p.chemin);
    });
  }
  // Ouverture : les IMAGES en plein écran dans l'app (visionneuse) ; les autres
  // fichiers via le navigateur/lecteur système.
  const ouvrir = (p) => {
    if (!p?.url) return;
    if (estImage(p)) setZoom(p.url);
    else Linking.openURL(p.url).catch(() => {});
  };

  return (
    <View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.bande} keyboardShouldPersistTaps="handled">
        {liste.map((p, i) => (
          <View key={(p.chemin || p.url) + i} style={s.item}>
            <TouchableOpacity activeOpacity={0.8} onPress={() => ouvrir(p)} onLongPress={lecture ? undefined : () => retirer(i)} delayLongPress={450}>
              {estImage(p)
                ? <Image source={{ uri: p.url }} style={s.vignette} />
                : <View style={s.fichier}><Text style={s.fichierIc}>📎</Text><Text style={s.fichierNom} numberOfLines={2}>{p.nom || t('budget.fichier')}</Text></View>}
            </TouchableOpacity>
            {!lecture && (
              <TouchableOpacity style={s.retirer} onPress={() => retirer(i)} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>
                <Text style={s.retirerTxt}>✕</Text>
              </TouchableOpacity>
            )}
          </View>
        ))}
        {!lecture && (
          <>
            {!!ImagePicker && Platform.OS !== 'web' && (
              <TouchableOpacity style={s.ajout} onPress={() => ajouterImage(true)} disabled={busy}><Text style={s.ajoutIc}>📷</Text><Text style={s.ajoutTxt}>{t('inventaire.photo')}</Text></TouchableOpacity>
            )}
            {!!ImagePicker && (
              <TouchableOpacity style={s.ajout} onPress={() => ajouterImage(false)} disabled={busy}><Text style={s.ajoutIc}>🖼️</Text><Text style={s.ajoutTxt}>{t('repas.photoGalerie')}</Text></TouchableOpacity>
            )}
            {!!DocumentPicker && (
              <TouchableOpacity style={s.ajout} onPress={ajouterFichier} disabled={busy}><Text style={s.ajoutIc}>📎</Text><Text style={s.ajoutTxt}>{t('budget.fichier')}</Text></TouchableOpacity>
            )}
          </>
        )}
        {busy && <View style={s.ajout}><ActivityIndicator color={UI.accent} /></View>}
      </ScrollView>
      {!!err && <Text style={s.err}>{err}</Text>}
      <VisionneuseImage url={zoom} onFermer={() => setZoom(null)} />
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  bande: { flexDirection: 'row', gap: 8, paddingVertical: 2 },
  item: { position: 'relative' },
  vignette: { width: 74, height: 74, borderRadius: 10, backgroundColor: UI.champ },
  fichier: { width: 74, height: 74, borderRadius: 10, backgroundColor: UI.champ, borderWidth: 0.5, borderColor: UI.champBord, alignItems: 'center', justifyContent: 'center', padding: 6, gap: 3 },
  fichierIc: { fontSize: 20 },
  fichierNom: { fontSize: 9, color: UI.muted, textAlign: 'center', fontWeight: '600' },
  retirer: { position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 10, backgroundColor: UI.danger, alignItems: 'center', justifyContent: 'center' },
  retirerTxt: { color: '#fff', fontSize: 12, fontWeight: '800' },
  ajout: { width: 74, height: 74, borderRadius: 10, borderWidth: 1, borderColor: UI.border, borderStyle: 'dashed', alignItems: 'center', justifyContent: 'center', gap: 2 },
  ajoutIc: { fontSize: 20 },
  ajoutTxt: { fontSize: 10.5, color: UI.muted, fontWeight: '600' },
  err: { fontSize: 11.5, color: UI.danger, marginTop: 6 },
});
